import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useTranslation } from "react-i18next";
import type { AppointmentStatus as AppointmentStatusOption } from "@/hooks/useAppointmentStatuses";
import {
  getFirstActiveStatusName,
  getStatusColor,
} from "@/lib/utils/clinic-appointment-status";

type StatusMode = "appointment" | "grooming";

const APPOINTMENT_STATUS_OPTIONS = [
  "agendado",
  "confirmado",
  "em_atendimento",
  "finalizado",
  "cancelado",
  "faltou",
] as const;

const GROOMING_STATUS_OPTIONS = [
  "agendado",
  "confirmado",
  "no_banho",
  "na_tosa",
  "secando",
  "finalizado",
  "cancelado",
  "faltou",
] as const;

const FALLBACK_LABEL_KEYS: Record<string, string> = {
  agendado: "statusAgendado",
  confirmado: "statusConfirmado",
  em_atendimento: "statusEmAtendimento",
  finalizado: "statusFinalizado",
  cancelado: "statusCancelado",
  faltou: "statusFaltou",
  no_banho: "statusNoBanho",
  na_tosa: "statusNaTosa",
  secando: "statusSecando",
};

interface AppointmentStatusSidePanelProps {
  title?: string;
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  mode: StatusMode;
  statuses?: AppointmentStatusOption[];
}

export function AppointmentStatusSidePanel({
  title,
  value,
  onChange,
  disabled,
  mode,
  statuses,
}: AppointmentStatusSidePanelProps) {
  const { t } = useTranslation("calendar");
  const fallbackOptions = mode === "grooming" ? GROOMING_STATUS_OPTIONS : APPOINTMENT_STATUS_OPTIONS;
  const orderedStatuses = statuses && statuses.length > 0
    ? [...statuses].sort((left, right) => left.sort_order - right.sort_order || left.name.localeCompare(right.name))
    : undefined;
  const options =
    orderedStatuses && orderedStatuses.length > 0
      ? orderedStatuses.filter((status) => status.is_active).map((status) => status.name)
      : fallbackOptions;
  const resolvedValue = value || getFirstActiveStatusName(orderedStatuses, options[0] ?? "");
  const resolvedTitle = title ?? t("appointmentStatusTitle");

  return (
    <section className="rounded-lg border border-border/60 bg-white p-4">
      <div className="flex items-center gap-2">
        <p className="text-sm font-semibold text-foreground">{resolvedTitle}</p>
      </div>
      <div className="mt-3 space-y-3">
        <Select value={resolvedValue} onValueChange={onChange} disabled={disabled}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder={t("selectStatusPlaceholder")} />
          </SelectTrigger>
          <SelectContent>
            {options.map((status) => {
              const fromDb = orderedStatuses?.find((s) => s.name === status)?.display_name;
              const label = fromDb ?? t(FALLBACK_LABEL_KEYS[status] ?? status);
              return (
                <SelectItem
                  key={status}
                  value={status}
                  className="pl-3 data-[state=checked]:bg-secondary data-[state=checked]:text-secondary-foreground [&>span:first-child]:hidden"
                >
                  <span className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: getStatusColor(status, statuses) }} />
                    <span>{label}</span>
                  </span>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>
    </section>
  );
}
